import React from 'react';
import { View, Text as PdfText } from '@react-pdf/renderer';
import { useRenderTarget } from './RenderTarget';
import { colors, fontFamily, typeScale } from '../tokens/designTokens';
import type { Severity } from '../tokens/designTokens';
import { PdfSection, PdfDataTable, PdfChip } from './PdfPrimitives';
import { ScreenSection, ScreenDataTable, ScreenChip } from './ScreenPrimitives';

// ---------------------------------------------------------------------------
// Token-driven scales. PDF values are points (react-pdf default unit).
// Screen values are pixels. Unitless numbers passed through unchanged work for
// padding/margin/width/height/gap on both — they just look slightly different
// at the same number (1pt ≈ 1.33px). Token names normalise that.
// ---------------------------------------------------------------------------

export type TextSize = 'micro' | 'caption' | 'bodySm' | 'body' | 'bodyLg' | 'h4' | 'h3' | 'h2';
export type TextTone = 'default' | 'muted' | 'mutedSoft' | 'ink00' | 'ink40' | 'accent' | 'pos' | 'neg' | 'warn';
export type TextFamily = 'body' | 'mono' | 'bold' | 'display';
export type TextWeight = 400 | 500 | 600 | 700;

const sizePt: Record<TextSize, number> = {
    micro: typeScale.micro,
    caption: typeScale.caption,
    bodySm: typeScale.bodySm,
    body: typeScale.body,
    bodyLg: typeScale.bodyLg,
    h4: typeScale.h4,
    h3: typeScale.displayH3,
    h2: typeScale.displayH2,
};

const sizePx: Record<TextSize, number> = {
    micro: 11,
    caption: 12,
    bodySm: 12.5,
    body: 13.5,
    bodyLg: 14,
    h4: 14,
    h3: 18,
    h2: 24,
};

const toneColor: Record<TextTone, string> = {
    default: colors.ink10,
    muted: colors.muted,
    mutedSoft: colors.mutedSoft,
    ink00: colors.ink00,
    ink40: colors.ink40,
    accent: colors.accent,
    pos: colors.pos,
    neg: colors.neg,
    warn: colors.warn,
};

const familyPdf = (f?: TextFamily) =>
    f === 'mono' ? fontFamily.mono : f === 'bold' ? fontFamily.bodyBold : f === 'display' ? fontFamily.display : fontFamily.body;

const familyScreen = (f?: TextFamily) =>
    f === 'mono' ? "'Geist Mono', monospace" : f === 'display' ? "'Fraunces', Georgia, serif" : 'inherit';

// ---------------------------------------------------------------------------
// Layout primitives
// ---------------------------------------------------------------------------

type CommonProps = {
    children?: React.ReactNode;
    flex?: number;
    width?: number | string;
    height?: number | string;
    padding?: number;
    paddingHorizontal?: number;
    paddingVertical?: number;
    margin?: number;
    marginTop?: number;
    marginBottom?: number;
    marginLeft?: number;
    marginRight?: number;
    background?: string;
    borderRadius?: number;
    overflow?: 'hidden' | 'visible';
    /** PDF-only: prevent the view from breaking across pages. Screen ignores. */
    wrap?: boolean;
    /** PDF-only: keep at least N points with the next sibling. Screen ignores. */
    minPresenceAhead?: number;
    /** PDF-only: force a page break before this view. Screen ignores. */
    breakBefore?: boolean;
};

const baseStyle = (p: CommonProps): Record<string, unknown> => {
    const s: Record<string, unknown> = {};
    if (p.flex != null) s.flex = p.flex;
    if (p.width != null) s.width = p.width;
    if (p.height != null) s.height = p.height;
    if (p.padding != null) s.padding = p.padding;
    if (p.paddingHorizontal != null) s.paddingHorizontal = p.paddingHorizontal;
    if (p.paddingVertical != null) s.paddingVertical = p.paddingVertical;
    if (p.margin != null) s.margin = p.margin;
    if (p.marginTop != null) s.marginTop = p.marginTop;
    if (p.marginBottom != null) s.marginBottom = p.marginBottom;
    if (p.marginLeft != null) s.marginLeft = p.marginLeft;
    if (p.marginRight != null) s.marginRight = p.marginRight;
    if (p.background) s.backgroundColor = p.background;
    if (p.borderRadius != null) s.borderRadius = p.borderRadius;
    if (p.overflow) s.overflow = p.overflow;
    return s;
};

// react-pdf uses paddingHorizontal/paddingVertical; CSS uses paddingLeft+paddingRight etc.
// Translate when emitting screen styles.
const screenify = (s: Record<string, unknown>): React.CSSProperties => {
    const out: Record<string, unknown> = { ...s };
    if (out.paddingHorizontal != null) {
        out.paddingLeft = out.paddingHorizontal;
        out.paddingRight = out.paddingHorizontal;
        delete out.paddingHorizontal;
    }
    if (out.paddingVertical != null) {
        out.paddingTop = out.paddingVertical;
        out.paddingBottom = out.paddingVertical;
        delete out.paddingVertical;
    }
    return out as React.CSSProperties;
};

const pdfHints = (p: CommonProps): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    if (p.wrap === false) out.wrap = false;
    if (p.minPresenceAhead != null) out.minPresenceAhead = p.minPresenceAhead;
    if (p.breakBefore) out.break = true;
    return out;
};

export const Box: React.FC<CommonProps> = (p) => {
    const target = useRenderTarget();
    const style = baseStyle(p);
    if (target === 'pdf') {
        return (
            <View style={style as any} {...pdfHints(p)}>
                {p.children}
            </View>
        );
    }
    return <div style={screenify(style)}>{p.children}</div>;
};

type StackProps = CommonProps & {
    gap?: number;
    align?: 'flex-start' | 'center' | 'flex-end' | 'stretch' | 'baseline';
    justify?: 'flex-start' | 'center' | 'flex-end' | 'space-between';
};

export const Stack: React.FC<StackProps> = (p) => {
    const target = useRenderTarget();
    const style = baseStyle(p);
    style.flexDirection = 'column';
    if (p.gap != null) style.gap = p.gap;
    if (p.align) style.alignItems = p.align;
    if (p.justify) style.justifyContent = p.justify;
    if (target === 'pdf') {
        return (
            <View style={style as any} {...pdfHints(p)}>
                {p.children}
            </View>
        );
    }
    return <div style={{ display: 'flex', ...screenify(style) }}>{p.children}</div>;
};

type RowProps = StackProps & {
    flexWrap?: boolean;
};

export const Row: React.FC<RowProps> = (p) => {
    const target = useRenderTarget();
    const style = baseStyle(p);
    style.flexDirection = 'row';
    if (p.gap != null) style.gap = p.gap;
    if (p.align) style.alignItems = p.align;
    if (p.justify) style.justifyContent = p.justify;
    if (p.flexWrap) style.flexWrap = 'wrap';
    if (target === 'pdf') {
        return (
            <View style={style as any} {...pdfHints(p)}>
                {p.children}
            </View>
        );
    }
    return <div style={{ display: 'flex', ...screenify(style) }}>{p.children}</div>;
};

// ---------------------------------------------------------------------------
// Text primitive — the "T" name keeps it short in module code.
// ---------------------------------------------------------------------------

type TextStyleProps = {
    children?: React.ReactNode;
    size?: TextSize;
    tone?: TextTone;
    family?: TextFamily;
    weight?: TextWeight;
    transform?: 'uppercase' | 'none';
    letterSpacing?: number;
    lineHeight?: number;
    align?: 'left' | 'right' | 'center';
    flex?: number;
    width?: number | string;
    marginTop?: number;
    marginBottom?: number;
    marginLeft?: number;
    marginRight?: number;
};

export const T: React.FC<TextStyleProps> = (p) => {
    const target = useRenderTarget();
    if (target === 'pdf') {
        const style: Record<string, unknown> = {
            fontSize: p.size ? sizePt[p.size] : typeScale.body,
            color: p.tone ? toneColor[p.tone] : colors.ink10,
            fontFamily: familyPdf(p.family),
        };
        if (p.weight) style.fontWeight = p.weight;
        if (p.transform === 'uppercase') style.textTransform = 'uppercase';
        if (p.letterSpacing != null) style.letterSpacing = p.letterSpacing;
        if (p.lineHeight != null) style.lineHeight = p.lineHeight;
        if (p.align) style.textAlign = p.align;
        if (p.flex != null) style.flex = p.flex;
        if (p.width != null) style.width = p.width;
        if (p.marginTop != null) style.marginTop = p.marginTop;
        if (p.marginBottom != null) style.marginBottom = p.marginBottom;
        if (p.marginLeft != null) style.marginLeft = p.marginLeft;
        if (p.marginRight != null) style.marginRight = p.marginRight;
        return <PdfText style={style as any}>{p.children}</PdfText>;
    }
    const style: React.CSSProperties = {
        fontSize: p.size ? sizePx[p.size] : 13.5,
        color: p.tone ? toneColor[p.tone] : colors.ink10,
        fontFamily: familyScreen(p.family),
    };
    if (p.weight) style.fontWeight = p.weight;
    if (p.transform === 'uppercase') style.textTransform = 'uppercase';
    if (p.letterSpacing != null) style.letterSpacing = p.letterSpacing;
    if (p.lineHeight != null) style.lineHeight = p.lineHeight;
    if (p.align) style.textAlign = p.align;
    if (p.flex != null) style.flex = p.flex;
    if (p.width != null) style.width = p.width;
    if (p.marginTop != null) style.marginTop = p.marginTop;
    if (p.marginBottom != null) style.marginBottom = p.marginBottom;
    if (p.marginLeft != null) style.marginLeft = p.marginLeft;
    if (p.marginRight != null) style.marginRight = p.marginRight;
    return <span style={style}>{p.children}</span>;
};

// ---------------------------------------------------------------------------
// Composite primitives — these dispatch to the existing Pdf*/Screen* pairs.
// Modules call these instead of importing from PdfPrimitives or ScreenPrimitives.
// ---------------------------------------------------------------------------

type SectionProps = {
    kicker?: string;
    title: string;
    subtitle?: string;
    index?: string;
    children: React.ReactNode;
    breakBefore?: boolean;
    minPresenceAhead?: number;
};

export const Section: React.FC<SectionProps> = (p) => {
    const target = useRenderTarget();
    if (target === 'pdf') {
        return (
            <PdfSection
                kicker={p.kicker}
                title={p.title}
                subtitle={p.subtitle}
                index={p.index}
                breakBefore={p.breakBefore}
                minPresenceAhead={p.minPresenceAhead}
            >
                {p.children}
            </PdfSection>
        );
    }
    return (
        <ScreenSection kicker={p.kicker} title={p.title} subtitle={p.subtitle} index={p.index}>
            {p.children}
        </ScreenSection>
    );
};

type Column = { key: string; header: string; width?: number | string; align?: 'left' | 'right' | 'center' };

type DataTableProps = {
    columns: Column[];
    rows: Array<Record<string, React.ReactNode>>;
    zebra?: boolean;
    /** PDF-only: max rows per atomic chunk. Screen ignores. */
    /** @deprecated kept on the prop type for caller-side compatibility — the
     *  PDF table no longer chunks (rows flow and break naturally now). */
    rowsPerChunk?: number;
};

export const DataTable: React.FC<DataTableProps> = (p) => {
    const target = useRenderTarget();
    if (target === 'pdf') {
        return <PdfDataTable columns={p.columns} rows={p.rows} zebra={p.zebra} />;
    }
    return <ScreenDataTable columns={p.columns} rows={p.rows} zebra={p.zebra} />;
};

type ChipTone = Severity | 'info' | 'brand' | 'muted' | 'accent';

export const Chip: React.FC<{ tone: ChipTone; children: React.ReactNode }> = ({ tone, children }) => {
    const target = useRenderTarget();
    if (target === 'pdf') return <PdfChip tone={tone}>{children}</PdfChip>;
    return <ScreenChip tone={tone}>{children}</ScreenChip>;
};

export const Divider: React.FC<{ marginVertical?: number }> = ({ marginVertical = 8 }) => {
    const target = useRenderTarget();
    if (target === 'pdf') {
        return <View style={{ height: 0.5, backgroundColor: colors.rule, marginTop: marginVertical, marginBottom: marginVertical }} />;
    }
    return <div style={{ height: 1, background: colors.rule, marginTop: marginVertical, marginBottom: marginVertical }} />;
};
